import React, { useState } from 'react';
import { generateProductDescriptionAI } from '../../services/api'; // Importa a função da API
import { getImageUrl } from '../../utils/imageUtils';
import './ProductEditForm.css';

const ProductEditForm = ({ product, onSave, onCancel }) => {
  // Valores iniciais para o formulário
  const [formData, setFormData] = useState({
    code: product.code?.toString() || '',
    name: product.name || '',
    price: parseFloat(product.price) || 0,
    salePrice: parseFloat(product.salePrice || 0),
    stock: parseInt(product.stock) || 0,
    active: product.active === true,
    description: product.description || '',
    tag: product.tag || '',
    category: product.category || '',
    id: product.id || ''
  });
  
  const [saving, setSaving] = useState(false);
  const [isGeneratingDesc, setIsGeneratingDesc] = useState(false); // Estado para loading da IA
  
  // Função para lidar com mudanças nos campos do formulário
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Determinar o valor correto com base no tipo de campo
    const fieldValue = type === 'checkbox' ? checked : value;
    
    // Atualizar o estado do formulário
    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));
    
    console.log(`Campo ${name} alterado para:`, fieldValue);
  };
  
  // Função para salvar o produto
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Preparar dados para envio
      const productData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        salePrice: parseFloat(formData.salePrice) || 0,
        stock: parseInt(formData.stock) || 0,
        active: formData.active === true
      };
      
      console.log('Salvando produto:', productData);
      
      // Chamar a função de salvamento
      await onSave(productData);
      console.log('Produto salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      alert(`Erro ao salvar produto: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Função para gerar descrição com IA
  const handleGenerateAIDescription = async () => {
    if (!formData.name) {
      alert('Por favor, preencha o nome do produto antes de gerar a descrição.');
      return;
    }
    setIsGeneratingDesc(true);
    try {
      const productDataForAI = {
        productName: formData.name,
        category: formData.category || '', // Envia categoria se houver
        // keywords: [], // Você pode adicionar um campo para palavras-chave se desejar
      };
      const result = await generateProductDescriptionAI(productDataForAI);
      if (result && result.description) {
        setFormData(prev => ({
          ...prev,
          description: result.description
        }));
      } else {
        alert('Não foi possível obter a descrição da IA.');
      }
    } catch (error) {
      console.error('Erro ao gerar descrição com IA:', error);
      alert(`Erro ao gerar descrição: ${error.message || 'Falha na comunicação.'}`);
    } finally {
      setIsGeneratingDesc(false);
    }
  };

  return (
    <div className="product-edit-form">
      <h3>Editar Produto</h3>
      
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          {/* Imagem e código do produto */}
          <div className="form-group">
            <div className="product-image">
              <img 
                src={getImageUrl(`/imagens/${product.code}.png`)} 
                alt={product.name}
                onError={(e) => {
                  e.target.src = getImageUrl(`/imagens/no-image.png`);
                }}
              />
            </div>
            <div className="product-code">
              <label>Código:</label>
              <span>{product.code}</span>
            </div>
          </div>
          
          {/* Nome */}
          <div className="form-group">
            <label htmlFor="name">Nome:</label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              autoComplete="off"
              required
            />
          </div>
          
          {/* Marca */}
          <div className="form-group">
            <label htmlFor="tag">Marca:</label>
            <input
              id="tag"
              type="text"
              name="tag"
              value={formData.tag}
              onChange={handleChange}
              className="form-control"
              autoComplete="off"
            />
          </div>
          
          {/* Categoria */}
          <div className="form-group">
            <label htmlFor="category">Categoria:</label>
            <input
              id="category"
              type="text"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="form-control"
              autoComplete="off"
            />
          </div>
          
          {/* Preço */}
          <div className="form-group">
            <label htmlFor="price">Preço:</label>
            <input
              id="price"
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="form-control"
              required
            />
          </div>
          
          {/* Preço Promocional */}
          <div className="form-group">
            <label htmlFor="salePrice">Preço Promocional:</label>
            <input
              id="salePrice"
              type="number"
              name="salePrice"
              value={formData.salePrice}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="form-control"
            />
          </div>
          
          {/* Estoque */}
          <div className="form-group">
            <label htmlFor="stock">Estoque:</label>
            <input
              id="stock"
              type="number"
              name="stock"
              value={formData.stock}
              onChange={handleChange}
              min="0"
              className="form-control"
              required
            />
          </div>
          
          {/* Descrição */}
          <div className="form-group full-width">
            <label htmlFor="description">Descrição:</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="form-control"
            />
            <button 
              type="button"
              onClick={handleGenerateAIDescription}
              disabled={isGeneratingDesc || saving}
              className="btn-ai-generate"
            >
              {isGeneratingDesc ? 'Gerando...' : 'Gerar Descrição com IA'}
            </button>
          </div>
          
          {/* Ativo */}
          <div className="form-group">
            <label htmlFor="active" className="checkbox-label">
              <input
                id="active"
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />
              <span>Ativo</span>
            </label>
          </div>
        </div>
        
        {/* Botões de ação */}
        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-save" 
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
          <button 
            type="button" 
            className="btn-cancel" 
            onClick={onCancel} 
            disabled={saving}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductEditForm;
